import { useState, useRef, useEffect } from "react";
import { Smile, Send, MoreVertical } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useChatSocket } from "@/hooks/useChatSocket";
import { useSearchParams } from "react-router-dom";
import {
  ChatMessageDTO,
  useGetMessagesQuery,
  useGetRoomDetailsQuery,
  useGetUserOnlineOfflineStatusQuery,
  useMarkRoomReadMutation,
  useSendMessageMutation,
} from "@/redux/feature/chat/chatApi";
import {
  joinChatRoom,
  leaveChatRoom,
  sendTypingStatus,
} from "@/services/chatSocketService";
import { useGlobalContext } from "@/context/GlobalContext";
import { TChatRoom } from "@/redux/feature/chat/chatType";
import toast from "react-hot-toast";
export type TChatMessage = {
  id: string;
  sender: "client" | "creator";
  senderName: string;
  content: string;
  timestamp: Date;
  isBlocked?: boolean;
  warningText?: string;
};

interface Contact {
  id: string;
  name: string;
  avatar?: string;
  isOnline?: boolean;
}
const formatTime = (date: Date) =>
  date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

interface ChatWindowProps {
  contact: Contact | null;
  // messages: TChatMessage[];
  isTyping?: boolean;
  onSendMessage: (content: string) => void;
  className?: string;
}

export function ChatWindow({ contact, className }: ChatWindowProps) {
  const { userData } = useGlobalContext();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const myRole = userData?.role as "client" | "creator"; // for creator end => "creator"
  const otherRole = myRole === "client" ? "creator" : "client";

  const [isTyping, setIsTyping] = useState(false);
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get("room");
  const [otherLastReadAtLive, setOtherLastReadAtLive] = useState<Date | null>(
    null,
  );
  const { socket, connected, userId } = useChatSocket(true);
  const [messages, setMessages] = useState<TChatMessage[]>([]);
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  const optimisticMessagesRef = useRef<Map<string, TChatMessage>>(new Map());

  const { data: historyData } = useGetMessagesQuery(
    roomId ? { roomId, limit: 50, page: 1 } : (undefined as any),
    { skip: !roomId },
  );

  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatRoomId = historyData?.data?.messages?.[0]?.chat_room_id;
  const { data: roomDto } = useGetRoomDetailsQuery(chatRoomId, {
    skip: !chatRoomId,
  });
  const room = roomDto?.data;
  console.log(room);
  const clientId = room?.client_id?._id;
  const creatorId = room?.creator_id?._id;

  const otherUserId =
    userId && clientId && creatorId
      ? userId === clientId
        ? creatorId
        : clientId
      : undefined;

  const otherLastReadAtISO =
    userId && clientId && creatorId
      ? userId === clientId
        ? room?.creator_last_read_at
        : room?.client_last_read_at
      : null;
  const otherLastReadAt = otherLastReadAtISO
    ? new Date(otherLastReadAtISO)
    : null;
  const { data: OnlineStatusDTO } = useGetUserOnlineOfflineStatusQuery(
    otherUserId,
    { skip: !otherUserId },
  );

  const onLineStatus = OnlineStatusDTO?.data?.online;
  const [markRoomRead] = useMarkRoomReadMutation();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    setOtherLastReadAtLive(otherLastReadAt);
  }, [otherLastReadAtISO]);

  useEffect(() => {
    if (!roomId) return;
    if (!connected) return;
    joinChatRoom(roomId);
    return () => {
      leaveChatRoom(roomId);
    };
  }, [roomId, connected]);

  // Reset seen IDs and optimistic messages when room changes
  useEffect(() => {
    seenMessageIdsRef.current = new Set();
    optimisticMessagesRef.current = new Map();
  }, [roomId]);

  // Load message history when available
  useEffect(() => {
    if (!historyData?.data?.messages) return;
    const mapped = historyData.data.messages.map((m: ChatMessageDTO) => {
      const senderId = (m.sender_id?._id ?? m.sender_id)?.toString?.() ?? "";
      const msg: TChatMessage = {
        id: m._id,
        senderName: m?.sender_id?.fullName || "",
        sender:
          senderId && userId && senderId === userId
            ? ("client" as const)
            : ("creator" as const),
        content: m.content,
        timestamp: new Date(m.timestamp),
      };
      seenMessageIdsRef.current.add(m._id);
      return msg;
    });
    setMessages(mapped);
  }, [historyData, userId]);

  // Realtime receiveMessage
  useEffect(() => {
    if (!socket) return;

    const onReceiveMessage = (m: any) => {
      console.log('📨 Message received:', m);
      
      // Extract room ID - prioritize top-level room_id from backend
      const incomingRoomId =
        m?.room_id ?? m?.chat_room_id?.room_id ?? m?.chat_room_id ?? "";
      
      console.log('🔍 Incoming room:', incomingRoomId, 'Current room:', roomId);
      
      // Only filter by room if we're in a specific room
      if (roomId && incomingRoomId && String(incomingRoomId) !== String(roomId)) {
        console.log('❌ Room mismatch, skipping');
        return;
      }

      const messageId = m?._id ?? Date.now().toString();
      
      // Skip if we've already seen this message
      if (seenMessageIdsRef.current.has(messageId)) {
        console.log('⏭️ Already seen message:', messageId);
        return;
      }
      
      const senderId = (m?.sender_id?._id ?? m?.sender_id)?.toString?.() ?? "";
      const isFromMe = senderId && userId && senderId === userId;
      const messageContent = m?.content ?? "";

      console.log('👤 From me:', isFromMe, 'Sender:', senderId, 'Me:', userId);
      
      // If this is our own message, check if we have an optimistic version with matching content
      if (isFromMe && optimisticMessagesRef.current.size > 0) {
        // Find optimistic message with matching content
        let matchedOptimisticId: string | null = null;
        for (const [tempId, optimisticMsg] of optimisticMessagesRef.current.entries()) {
          if (optimisticMsg.content === messageContent) {
            matchedOptimisticId = tempId;
            break;
          }
        }
        
        if (matchedOptimisticId) {
          // Replace optimistic message with real one
          console.log('🔄 Replacing optimistic message');
          seenMessageIdsRef.current.add(messageId);
          optimisticMessagesRef.current.delete(matchedOptimisticId);
          
          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id === matchedOptimisticId) {
                return {
                  id: messageId,
                  senderName: m?.sender_id?.fullName || userData?.fullName || "",
                  sender: "client" as const,
                  content: messageContent,
                  timestamp: new Date(m?.timestamp ?? Date.now()),
                };
              }
              return msg;
            }),
          );
          return;
        }
      }
      
      // Add new message from others or if no optimistic version exists
      console.log('✅ Adding new message to UI');
      seenMessageIdsRef.current.add(messageId);
      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          senderName: m?.sender_id?.fullName || "",
          sender: isFromMe ? "client" : "creator",
          content: messageContent,
          timestamp: new Date(m?.timestamp ?? Date.now()),
        },
      ]);
    };

    const onUserTyping = ({ isTyping }: any) => {
      setIsTyping(Boolean(isTyping));
    };

    const onMessageDeleted = ({ messageId, hard }: any) => {
      console.log("Delted_mesage", messageId);
      if (hard) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        return;
      }

      // optional fallback
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, content: "" } : m)),
      );
    };
    const onMessageBlockedWarning = (payload: any) => {
      if (String(payload?.roomId || "") !== String(roomId || "")) return;
      const warningId = `blocked_${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: warningId,
          sender: "client",
          senderName: userData?.fullName || "You",
          content: payload?.content || "",
          timestamp: new Date(payload?.blockedAt || Date.now()),
          isBlocked: true,
          warningText:
            payload?.warningText ||
            "This kind of message can lead to a permanent ban from the platform.",
        },
      ]);
    };
    socket.on("receiveMessage", onReceiveMessage);
    socket.on("userTyping", onUserTyping);
    socket.on("messageDeleted", onMessageDeleted);
    socket.on("messageBlockedWarning", onMessageBlockedWarning);

    return () => {
      socket.off("receiveMessage", onReceiveMessage);
      socket.off("userTyping", onUserTyping);
      socket.off("messageDeleted", onMessageDeleted);
      socket.off("messageBlockedWarning", onMessageBlockedWarning);
    };
  }, [socket, userId, userData?.fullName]);

  useEffect(() => {
    if (!socket) return;

    const onRoomReadReceipt = ({ userId: readerId, readAt }: any) => {
      if (readerId === otherUserId) {
        setOtherLastReadAtLive(new Date(readAt));
      }
    };

    socket.on("roomReadReceipt", onRoomReadReceipt);
    return () => {
      socket.off("roomReadReceipt", onRoomReadReceipt);
    };
  }, [socket, otherUserId]);
  useEffect(() => {
    if (!socket) return;
    if (!connected) return;
    if (!roomId) return;
    if (!messages.length) return;

    const last = messages[messages.length - 1];

    if (last.sender !== myRole) {
      socket.emit("roomRead", { roomId });
      markRoomRead(roomId).catch(() => {});
    }
  }, [roomId, socket, connected, messages, myRole, markRoomRead]);

  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    if (!roomId) return;
    if (!otherUserId) return;

    const content = inputValue.trim();
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    
    // Add optimistic message immediately
    const optimisticMessage: TChatMessage = {
      id: tempId,
      sender: "client" as const,
      senderName: userData?.fullName || "You",
      content,
      timestamp: new Date(),
    };
    
    optimisticMessagesRef.current.set(tempId, optimisticMessage);
    setMessages((prev) => [...prev, optimisticMessage]);
    setInputValue("");

    sendTypingStatus(roomId, false);

    const basePayload: any = {
      receiver_id: otherUserId,
      content,
      message_type: "text",
    };

    if (room?.chat_type === "order_video") {
      basePayload.chat_type = "order_video";
      basePayload.order_id = room.order_id; // ✅ from room details
      basePayload.video_id = room.video_id; // ✅ from room details
    }

    try {
      const response = await sendMessage(basePayload).unwrap();
      const realMessageId = response?.data?._id;
      
      // If we got a real message ID from the API, update the optimistic message
      if (realMessageId && optimisticMessagesRef.current.has(tempId)) {
        seenMessageIdsRef.current.add(realMessageId);
        optimisticMessagesRef.current.delete(tempId);
        
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? {
                  ...msg,
                  id: realMessageId,
                }
              : msg,
          ),
        );
      }
    } catch (e) {
      const blocked = (e as any)?.data?.code === "CONTACT_MESSAGE_BLOCKED";
      if (blocked) {
        optimisticMessagesRef.current.delete(tempId);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? {
                  ...msg,
                  isBlocked: true,
                  warningText:
                    "This kind of message can lead to a permanent ban from the platform.",
                }
              : msg,
          ),
        );
        return;
      }

      // Remove optimistic message on error
      optimisticMessagesRef.current.delete(tempId);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      toast.error((e as any)?.data?.message || "Failed to send message");
    }
  };
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, isTyping]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getDeliveryStatus = (msg: TChatMessage) => {
    console.log("came here");
    if (msg.sender !== "client") {
      return null;
    }

    if (!otherLastReadAtLive) {
      return "Sent";
    }
    return otherLastReadAtLive.getTime() >= msg.timestamp.getTime()
      ? "Seen"
      : "Sent";
  };
  const lastOutgoingId = [...messages]
    .reverse()
    .find((m) => m.sender === "client")?.id;

  if (!contact) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center h-full bg-background",
          className,
        )}
      >
        <div className="text-center animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-1">
            Select a conversation
          </h3>
          <p className="text-sm text-muted-foreground">
            Choose a contact to start messaging
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full bg-background", className)}>
      {/* Header */}
      <header className="px-6 py-5 flex items-center justify-between border-b border-[#DFDFDF] bg-card animate-fade-in">
        <div className="flex items-center gap-4">
          <Avatar className="w-10 h-10">
            <AvatarImage src={contact.avatar} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {contact?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>

          <div className="leading-tight">
            <h6 className="text-xl font-medium tracking-normal text-foreground">
              {contact.name}
            </h6>

            <div className="flex items-center gap-1.5 mt-0.5">
              {onLineStatus && <span className="online-indicator" />}
              <span className="text-xs text-muted-foreground">
                {onLineStatus ? "Online" : "Offline"}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-[30px] py-[24px] scrollbar-thin">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <MessageBubble
              key={message.id}
              message={message}
              showSender={message.sender === "client"}
              style={{ animationDelay: `${index * 30}ms` }}
              deliveryStatus={getDeliveryStatus(message)}
            />
          ))}

          {isTyping && (
            <TypingIndicator
              name={contact?.name?.split(" ")[0]}
              avatar={contact.avatar}
            />
          )}

          {/* keep scrolling anchors unchanged */}
          <div ref={bottomRef} />
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="px-4 py-4 border-t border-border bg-card">
        <div className="flex items-center gap-3 bg-muted rounded-full px-4 py-2 transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20">
          <button className="p-1 hover:bg-muted-foreground/10 rounded-full transition-colors">
            <Smile className="w-5 h-5 text-muted-foreground" />
          </button>

          <input
            ref={inputRef}
            type="text"
            placeholder="Reply ..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />

          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200",
              inputValue.trim()
                ? "bg-primary text-primary-foreground hover:opacity-90 scale-100"
                : "bg-muted-foreground/20 text-muted-foreground scale-95",
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  showSender,
  style,
  deliveryStatus,
}: {
  message: TChatMessage;
  showSender?: boolean;
  style?: React.CSSProperties;
  deliveryStatus?: "Sent" | "Seen" | null;
}) {
  const isOutgoing = message.sender === "client";

  return (
    <div
      className={cn(
        "flex w-full mb-[30px]",
        isOutgoing ? "justify-end" : "justify-start",
      )}
    >
      {/* Opponent Message */}
      {!isOutgoing && (
        <div className="flex gap-3 items-start max-w-[320px]">
          {showSender && (
            <Avatar className="w-10 h-10">
              <AvatarFallback>
                {message.senderName?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
          )}

          <div>
            {showSender && (
              <p className="text-base font-medium text-[#0D082C] mb-1">
                {message.senderName}
              </p>
            )}

            <div className="bg-[#FFF5EF] text-[#0D082C] text-base font-normal px-[15px] py-[10px] rounded-[10px] tracking-[-1.2%]">
              {message.content}
            </div>

            <p className="text-[14px] text-[#0D082C66] mt-1 text-right">
              {formatTime(message.timestamp)}
            </p>
          </div>
        </div>
      )}

      {/* Own Message */}
      {isOutgoing && (
        <div className="max-w-[320px] text-right">
          <div className="bg-black text-white text-base px-[15px] py-[10px] rounded-[10px]">
            {message.content}
          </div>
          {message.isBlocked ? (
            <p className="text-xs text-[#b42318] mt-1">{message.warningText}</p>
          ) : null}

          <p className="text-sm text-[#0D082C66] mt-1">
            {formatTime(message.timestamp)}
            {/* {deliveryStatus && ` • ${deliveryStatus}`} */}
          </p>
        </div>
      )}
    </div>
  );
}

function TypingIndicator({ name, avatar }: { name: string; avatar?: string }) {
  return (
    <div className="flex gap-3 items-start mb-[30px]">
      <Avatar className="w-8 h-8 mt-1">
        <AvatarImage src={avatar} />
        <AvatarFallback>{name.charAt(0)}</AvatarFallback>
      </Avatar>

      <div>
        <p className="text-[16px] font-normal text-[#0D082C] mb-1">{name}</p>

        <div className="bg-[#FFF5EF] px-[15px] py-[10px] rounded-[10px]">
          <div className="flex gap-1">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        </div>
      </div>
    </div>
  );
}
